import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  ArrowLeft,
  Users,
  Globe,
  Eye,
  EyeOff,
  ChevronDown,
  Shield,
  School,
  GraduationCap,
  User,
  HeartHandshake,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { AuthSplitLayout } from "../../components/auth/AuthSplitLayout";
import { handleApiError, showSuccessToast } from "../../utils/errorHandler";
import { ROUTES } from "../../utils/constants";
import { validateEmail } from "../../utils/validation";
import { normalizeLoginRoleSelection } from "../../auth/roleHeaders";
import {
  resolveAdminPostLoginPathFromRoles,
  resolvePostLoginPath,
  resolvePrimaryAdminRole,
} from "../../utils/postLoginRedirect";
import { shouldShowLoginWelcomeToast } from "../../utils/loginWelcomeToast";
import { resetLogoutRedirectState } from "../../utils/authSession";
import {
  getLastPortal,
  rememberPortalFromLoginRole,
  resolvePortalFromRole,
  syncSelectedRoleFromPortal,
} from "../../utils/loginPortal";
import { getPublicTenantIntent, setPublicTenantIntent, resolveHomeNavigationPath } from "../../utils/publicTenantHandoff";

interface LoginFormData {
  email: string;
  password: string;
}

type RoleKey = "dev_admin" | "school_admin" | "teacher" | "student" | "parent" | "";
type ModeKey = "admin" | "user";

const initialTouched = { email: false, password: false };

const TEXT = {
  home: "Home",
  title: "Login",
  subtitle: "Enter your email and password to continue.",
  emailRequired: "Email is required",
  invalidEmail: "Please enter a valid email",
  passwordRequired: "Password is required",
  forgot: "Forgot password?",
  start: "Sign in",
  entering: "Signing in...",
  welcome: "Welcome back",
  comingSoon: "Coming soon",
  google: "Continue with Google",
  sso: "Continue with SSO",
  soonNote: "More login options will be available soon.",
  noAccount: "Don't have an account?",
  createAccount: "Create account",
  emailLabel: "Email",
  emailPlaceholder: "Enter your email",
  passwordLabel: "Password",
  passwordPlaceholder: "Enter your password",
  showPassword: "Show password",
  hidePassword: "Hide password",
  selectRole: "Select Login Type",
  selected: "Selected",
};

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState(initialTouched);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // ✅ Role selection UI state — restore from last portal / selected role
  const [selectedRole, setSelectedRole] = useState<RoleKey>(() => {
    try {
      const fromPortal = getLastPortal();
      if (fromPortal) {
        syncSelectedRoleFromPortal(fromPortal);
      }
      const v = localStorage.getItem('tkm_selected_role');
      const portal = resolvePortalFromRole(v) ?? fromPortal;
      if (portal === 'student') return 'student';
      if (portal === 'teacher') return 'teacher';
      if (portal === 'parent') return 'parent';
      if (portal === 'school-admin') return 'school_admin';
      if (portal === 'admin') return 'dev_admin';
      return (v as RoleKey) ?? '';
    } catch {
      return '';
    }
  });

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("tenant_id") ?? undefined;

  // ✅ Decide which role options to show using query param:
  // /login?mode=admin  -> School Admin + Dev Team
  // /login?mode=user (or /login default) -> Parent + Student + Teacher
  const modeParam = searchParams.get("mode");
  const mode: ModeKey = modeParam === "admin" ? "admin" : "user";

  // ✅ Invite redirect (unchanged)
  useEffect(() => {
    resetLogoutRedirectState();
    if (import.meta.env.DEV) {
      console.log('[auth] Auth state after logout — login page mounted, logout redirect lock cleared');
    }
    const inviteToken = searchParams.get("invite_token") || searchParams.get("token");
    const tenantId = searchParams.get("tenant_id");
    if (inviteToken) {
      const params = new URLSearchParams();
      params.set("token", inviteToken);
      if (tenantId) params.set("tenant_id", tenantId);
      navigate(`${ROUTES.INVITE_ACCEPT}?${params.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // ✅ All roles (master list)
  const ALL_ROLE_OPTIONS = useMemo(
    () =>
      [
        { key: "school_admin" as const, label: "School Admin", icon: School, accent: "from-emerald-500 to-sky-500" },
        { key: "dev_admin" as const, label: "Dev Team", icon: Shield, accent: "from-fuchsia-500 to-sky-500" },

        { key: "parent" as const, label: "Parent", icon: HeartHandshake, accent: "from-rose-500 to-app-primary/75" },
        { key: "student" as const, label: "Student", icon: User, accent: "from-sky-500 to-indigo-500" },
        { key: "teacher" as const, label: "Teacher", icon: GraduationCap, accent: "from-amber-500 to-orange-500" },
      ] as const,
    []
  );

  // ✅ Filter dropdown options by mode
  const ROLE_OPTIONS = useMemo(() => {
    if (mode === "admin") return ALL_ROLE_OPTIONS.filter((r) => r.key === "school_admin" || r.key === "dev_admin");
    return ALL_ROLE_OPTIONS.filter((r) => r.key === "parent" || r.key === "student" || r.key === "teacher");
  }, [ALL_ROLE_OPTIONS, mode]);

  // ✅ Ensure selectedRole is valid for the current mode (prevents weird dropdown like your screenshot)
  useEffect(() => {
    const allowedKeys = ROLE_OPTIONS.map((r) => r.key) as string[];
    if (!allowedKeys.includes(String(selectedRole))) {
      // Default selection per mode
      setSelectedRole(mode === "admin" ? "school_admin" : "student");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, ROLE_OPTIONS]);

  const selectedRoleObj = useMemo(
    () => ROLE_OPTIONS.find((r) => r.key === selectedRole) || ROLE_OPTIONS[0],
    [ROLE_OPTIONS, selectedRole]
  );

  const {
    register: registerField,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const emailRegister = registerField("email", {
    required: t("auth.login.errors.emailRequired", { defaultValue: TEXT.emailRequired }),
    validate: (value) =>
      validateEmail(value) || t("auth.login.errors.invalidEmail", { defaultValue: TEXT.invalidEmail }),
  });

  const passwordRegister = registerField("password", {
    required: t("auth.login.errors.passwordRequired", { defaultValue: TEXT.passwordRequired }),
  });

  const showEmailError = (submitted || touched.email) && errors.email?.message;
  const showPasswordError = (submitted || touched.password) && errors.password?.message;

  // ✅ Reserve space so the page does NOT move up/down when error text appears
  const emailHelperText = showEmailError ? String(errors.email?.message) : " ";
  const passwordHelperText = showPasswordError ? String(errors.password?.message) : " ";

  // (no click-outside handler required for tabs)

  // ✅ When role changes: reset errors/touched/submitted so UX is clean
  useEffect(() => {
    setSubmitted(false);
    setTouched(initialTouched);
    setLoginError(null);
    reset(undefined, { keepValues: true });
  }, [selectedRole, reset]);

  const watchedEmail = watch("email");
  const watchedPassword = watch("password");
  useEffect(() => {
    setLoginError(null);
  }, [watchedEmail, watchedPassword]);

  const onSubmit = async (data: LoginFormData) => {
    if (import.meta.env.DEV) {
      console.log('[auth] Login button clicked');
    }
    setSubmitted(true);
    setIsLoading(true);
    try {
      if (import.meta.env.DEV) {
        console.log('[auth] Login validation passed');
      }
      const loginOptions =
        mode === "admin"
          ? { loginPortal: "admin" as const }
          : { expectedRole: normalizeLoginRoleSelection(String(selectedRole)) ?? undefined };
      const loggedInUser = await login(data.email, data.password, loginOptions);

      const roles = loggedInUser.roles ?? [];
      const onboardingComplete = loggedInUser.onboarding_complete ?? false;
      const platformChosen =
        mode === "admin"
          ? resolvePrimaryAdminRole(roles)
          : normalizeLoginRoleSelection(String(selectedRole));
      if (platformChosen) {
        try {
          localStorage.setItem('tkm_selected_role', platformChosen);
        } catch {
          /* ignore */
        }
        rememberPortalFromLoginRole(platformChosen);
      }

      let actualRoleLabel = '';
      if (mode === "admin") {
        const adminRole = resolvePrimaryAdminRole(roles);
        if (adminRole === 'DEV_TEAM') actualRoleLabel = 'Dev Team';
        else if (adminRole === 'SCHOOL_ADMIN') actualRoleLabel = 'School Admin';
      } else {
        try {
          const roleMap: Record<string, string> = {
            DEV_TEAM: 'Dev Team',
            SCHOOL_ADMIN: 'School Admin',
            TEACHER: 'Teacher',
            PARENT: 'Parent',
            STUDENT: 'Student',
          };
          const active = localStorage.getItem('tkm_selected_role') || '';
          if (active && roleMap[active]) {
            actualRoleLabel = roleMap[active];
          }
        } catch {
          actualRoleLabel = selectedRoleObj.label;
        }
      }

      if (shouldShowLoginWelcomeToast(onboardingComplete)) {
        const welcome = t("auth.login.welcome", { defaultValue: TEXT.welcome });
        showSuccessToast(actualRoleLabel ? `${welcome} • ${actualRoleLabel} Login` : welcome);
      }

      const returnTo = searchParams.get('returnTo');
      const fromLoc = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
      const fromPathname = fromLoc
        ? `${fromLoc.pathname || ''}${fromLoc.search || ''}`
        : null;
      const destination =
        mode === "admin"
          ? resolveAdminPostLoginPathFromRoles(roles, returnTo)
          : resolvePostLoginPath({
              returnTo,
              fromPathname,
              roles,
              onboardingComplete,
            });
      navigate(destination, { replace: true });
    } catch (error) {
      const message = handleApiError(error);
      setLoginError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const publicIntent = getPublicTenantIntent();
  const isMalayPresentation = Boolean(publicIntent?.isMalay);
  const homePath = resolveHomeNavigationPath();

  const [loginLang, setLoginLang] = useState<'ms' | 'en'>(() => {
    return (publicIntent?.language === 'en' ? 'en' : 'ms') as 'ms' | 'en';
  });

  const handleLanguageToggle = (nextLang: 'ms' | 'en') => {
    setLoginLang(nextLang);
    const currentIntent = getPublicTenantIntent();
    if (currentIntent?.isMalay) {
      setPublicTenantIntent({
        ...currentIntent,
        language: nextLang,
      });
    }
  };

  const loginTitle = isMalayPresentation
    ? (loginLang === 'en' ? "Sign In" : "Log Masuk")
    : t("auth.login.title", { defaultValue: TEXT.title });

  const loginSubtitle = isMalayPresentation
    ? (loginLang === 'en' ? "Enter your email and password to continue." : "Sila masukkan e-mel dan kata laluan anda untuk meneruskan.")
    : undefined;

  const emailLabel = isMalayPresentation
    ? (loginLang === 'en' ? "Email" : "E-mel")
    : t("auth.login.email", { defaultValue: TEXT.emailLabel });

  const emailPlaceholder = isMalayPresentation
    ? (loginLang === 'en' ? "Enter your email" : "Masukkan e-mel anda")
    : t("auth.login.emailPlaceholder", { defaultValue: TEXT.emailPlaceholder });

  const passwordLabel = isMalayPresentation
    ? (loginLang === 'en' ? "Password" : "Kata Laluan")
    : t("auth.login.password", { defaultValue: TEXT.passwordLabel });

  const passwordPlaceholder = isMalayPresentation
    ? (loginLang === 'en' ? "Enter your password" : "Masukkan kata laluan anda")
    : t("auth.login.passwordPlaceholder", { defaultValue: TEXT.passwordPlaceholder });

  const signInText = isMalayPresentation
    ? (loginLang === 'en' ? "Sign In" : "Log Masuk")
    : t("auth.login.signIn", { defaultValue: TEXT.start });

  const signingInText = isMalayPresentation
    ? (loginLang === 'en' ? "Signing in..." : "Sedang Log Masuk...")
    : t("auth.login.signingIn", { defaultValue: TEXT.entering });

  const loginEmailFieldClass = isMalayPresentation
    ? "h-14 rounded-2xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 shadow-lg text-base"
    : "h-14 rounded-2xl border-2 border-sky-300 focus:border-fuchsia-400 focus:ring-4 focus:ring-fuchsia-200/50 shadow-lg text-base";

  const loginPasswordFieldClass = isMalayPresentation
    ? "h-14 rounded-2xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 shadow-lg text-base"
    : "h-14 rounded-2xl border-2 border-orange-300 focus:border-rose-400 focus:ring-4 focus:ring-rose-200/50 shadow-lg text-base";

  const submitButtonClass = isMalayPresentation
    ? "from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 shadow-orange-500/25"
    : (mode === "admin" ? "from-emerald-500 to-sky-500" : selectedRoleObj.accent);

  return (
    <AuthSplitLayout showBackButton={false} tenantId={tenantId} panel="login">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(homePath)}
            className={`flex items-center gap-1.5 text-base font-semibold ${
              isMalayPresentation ? "text-orange-600 hover:text-orange-700" : "text-amber-600 hover:text-amber-700"
            } hover:scale-105 transition-all duration-200 -ml-2`}
            aria-label={t("common.home", { defaultValue: TEXT.home })}
          >
            <ArrowLeft className="h-5 w-5" />
            {isMalayPresentation ? (loginLang === 'en' ? "Home" : "Utama") : t("common.home", { defaultValue: TEXT.home })}
          </button>

          {isMalayPresentation && (
            <div className="flex items-center rounded-full bg-orange-100/80 dark:bg-gray-800 p-1 border border-orange-200/60 dark:border-gray-700">
              <button
                type="button"
                onClick={() => handleLanguageToggle('ms')}
                className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold transition-all ${
                  loginLang === 'ms'
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 hover:text-orange-600'
                }`}
              >
                <Globe className="h-3.5 w-3.5" />
                Bahasa Melayu
              </button>
              <button
                type="button"
                onClick={() => handleLanguageToggle('en')}
                className={`rounded-full px-3 py-1 text-xs font-bold transition-all ${
                  loginLang === 'en'
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 hover:text-orange-600'
                }`}
              >
                English
              </button>
            </div>
          )}
        </div>

        {isMalayPresentation && (
          <div className="flex items-center gap-3 rounded-2xl bg-orange-50 dark:bg-gray-800/80 p-3.5 border border-orange-200/70 dark:border-gray-700 shadow-sm">
            <div className="h-10 w-10 rounded-xl bg-white p-1 flex items-center justify-center ring-1 ring-orange-500/20 shadow-sm shrink-0">
              <img src="/images/mce-logo.png" alt="Marshall Cavendish Logo" className="h-full w-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-extrabold text-gray-900 dark:text-white">
                Marshall Cavendish Education
              </div>
              <div className="text-xs font-semibold text-orange-600 dark:text-orange-400">
                Pendidikan Marshall Cavendish
              </div>
            </div>
          </div>
        )}

        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white">
            {loginTitle}
          </h1>
          {loginSubtitle && (
            <p className="mt-1 text-sm font-medium text-gray-600 dark:text-gray-400">
              {loginSubtitle}
            </p>
          )}
        </div>

        {/* Role tabs: user portal only (Teacher / Student / Parent) */}
        {mode !== "admin" && (
          <div className="mt-4 w-full">
            <div className="w-full grid grid-cols-3 gap-3">
              {ROLE_OPTIONS.map((r) => {
                const Ico = r.icon;
                const active = selectedRole === r.key;
                const label = isMalayPresentation
                  ? (r.key === 'student'
                      ? (loginLang === 'en' ? 'Student' : 'Murid')
                      : r.key === 'teacher'
                      ? (loginLang === 'en' ? 'Teacher' : 'Guru')
                      : (loginLang === 'en' ? 'Parent' : 'Ibu Bapa'))
                  : r.label;

                return (
                  <button
                    key={r.key}
                    type="button"
                    data-testid={`login-role-tab-${r.key}`}
                    onClick={() => {
                      setSelectedRole(r.key);
                      try {
                        localStorage.setItem('tkm_selected_role', r.key);
                      } catch {
                        // ignore
                      }
                    }}
                    className={[
                      "h-12 rounded-xl text-sm font-semibold transition-all",
                      "flex items-center justify-center gap-2",
                      active
                        ? (isMalayPresentation ? "bg-orange-500 text-white shadow-md" : "bg-teal-600 text-white shadow-md")
                        : "bg-white/80 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-900/70",
                    ].join(" ")}
                    aria-pressed={active}
                  >
                    <Ico className="h-4 w-4" />
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Role tabs for mode === "admin" (School Admin / Dev Team) */}
        {mode === "admin" && (
          <div className="mt-4 w-full">
            <div className="w-full grid grid-cols-2 gap-3">
              {ROLE_OPTIONS.map((r) => {
                const Ico = r.icon;
                const active = selectedRole === r.key;
                const label = isMalayPresentation
                  ? (r.key === 'school_admin'
                      ? (loginLang === 'en' ? 'School Admin' : 'Pentadbir Sekolah')
                      : (loginLang === 'en' ? 'Dev Team' : 'Log Pembangun'))
                  : r.label;

                return (
                  <button
                    key={r.key}
                    type="button"
                    data-testid={`login-role-tab-${r.key}`}
                    onClick={() => {
                      setSelectedRole(r.key);
                      try {
                        localStorage.setItem('tkm_selected_role', r.key);
                      } catch {
                        // ignore
                      }
                    }}
                    className={[
                      "h-12 rounded-xl text-sm font-semibold transition-all",
                      "flex items-center justify-center gap-2",
                      active
                        ? (isMalayPresentation ? "bg-orange-500 text-white shadow-md" : "bg-teal-600 text-white shadow-md")
                        : "bg-white/80 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-900/70",
                    ].join(" ")}
                    aria-pressed={active}
                  >
                    <Ico className="h-4 w-4" />
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit, () => setSubmitted(true))}
          className="space-y-5"
          noValidate
        >
          {/* Social / SSO buttons removed per request */}
          {/* 
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-2xl border-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 shadow-md"
              disabled
              title={TEXT.comingSoon}
            >
              <Globe className="h-5 w-5 mr-2" />
              {TEXT.google}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-2xl border-2 border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-700 shadow-md"
              disabled
              title={TEXT.comingSoon}
            >
              <Users className="h-5 w-5 mr-2" />
              {TEXT.sso}
            </Button>

            <p className="text-center text-xs font-medium text-amber-600 bg-amber-50 px-3 py-2 rounded-xl">
              {TEXT.soonNote}
            </p>
          </div>
          */}

          <Input
            data-testid="login-email-input"
            label={emailLabel}
            type="email"
            {...emailRegister}
            onBlur={(e) => {
              emailRegister.onBlur(e);
              setTouched((prev) => ({ ...prev, email: true }));
            }}
            error={showEmailError ? errors.email?.message : undefined}
            helperText={emailHelperText}
            placeholder={emailPlaceholder}
            className={loginEmailFieldClass}
          />

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-base font-semibold text-app-text-muted">
                {passwordLabel}
              </label>

              <Link
                to={ROUTES.FORGOT_PASSWORD}
                data-testid="login-forgot-password-link"
                className="text-sm font-semibold bg-gradient-to-r from-rose-400 to-app-primary/75 bg-clip-text text-transparent hover:scale-105 transition-all"
              >
                {t("auth.login.forgot", { defaultValue: TEXT.forgot })}
              </Link>
            </div>

            <div className="relative">
              <Input
                id="login-password"
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                {...passwordRegister}
                onBlur={(e) => {
                  passwordRegister.onBlur(e);
                  setTouched((prev) => ({ ...prev, password: true }));
                }}
                error={showPasswordError ? errors.password?.message : undefined}
                helperText={passwordHelperText}
                placeholder={passwordPlaceholder}
                className={`${loginPasswordFieldClass} pr-12`}
              />

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label={
                  showPassword
                    ? t("auth.login.hidePassword", { defaultValue: TEXT.hidePassword })
                    : t("auth.login.showPassword", { defaultValue: TEXT.showPassword })
                }
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {loginError && (
              <p className="mt-1 text-sm text-red-400" role="alert">
                {loginError}
              </p>
            )}
          </div>

          <Button
            type="submit"
            data-testid="login-submit-button"
            className={`w-full h-14 bg-gradient-to-r ${submitButtonClass} text-lg font-bold rounded-3xl shadow-2xl text-white`}
            isLoading={isLoading}
          >
            {isLoading ? signingInText : signInText}
          </Button>
        </form>

        
      </div>
    </AuthSplitLayout>
  );
};
